import { toAppError } from '@repo/api-client';
import { useSession, useHttpClient } from '@repo/api-client/react';
import { CheckCircle2, ExternalLink, Loader2 } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { describeAppError } from '@/features/common/describeAppError';
import { AuthLayout } from '../auth/AuthLayout';
import { NotFoundScreen } from './NotFound';

interface InvitePreview {
  status: 'pending' | 'expired' | 'revoked' | 'accepted';
  orgName: string;
  inviterName: string;
  role: 'owner' | 'admin' | 'member';
  expiresAt: string;
}

type State =
  | { kind: 'loading' }
  | { kind: 'preview'; preview: InvitePreview }
  | { kind: 'missing' };

export function InviteAcceptScreen() {
  const { token = '' } = useParams<{ token: string }>();
  const { status: sessionStatus } = useSession();
  const navigate = useNavigate();
  const http = useHttpClient();
  const [state, setState] = useState<State>({ kind: 'loading' });
  const [accepting, setAccepting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!token) {
      setState({ kind: 'missing' });
      return;
    }
    let cancelled = false;
    axios
      .get<InvitePreview>(`/api/invites/preview/${encodeURIComponent(token)}`, {
        withCredentials: false,
      })
      .then(({ data }) => {
        if (!cancelled) setState({ kind: 'preview', preview: data });
      })
      .catch(() => {
        if (!cancelled) setState({ kind: 'missing' });
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  const accept = useCallback(async () => {
    setError(null);
    setAccepting(true);
    try {
      await http.post('/invites/accept', { token });
      navigate('/', { replace: true });
    } catch (err) {
      const copy = describeAppError(toAppError(err));
      setError(copy.message);
    } finally {
      setAccepting(false);
    }
  }, [http, navigate, token]);

  if (state.kind === 'loading') {
    return (
      <AuthLayout title="Loading invite…">
        <div className="flex justify-center py-4">
          <Loader2 className="size-8 animate-spin text-muted-foreground" />
        </div>
      </AuthLayout>
    );
  }
  if (state.kind === 'missing') {
    return <NotFoundScreen variant="invite" />;
  }

  const { preview } = state;

  if (preview.status === 'expired') {
    return (
      <AuthLayout
        title="Invite expired"
        subtitle={`This invite to ${preview.orgName} expired on ${new Date(preview.expiresAt).toLocaleDateString()}. Ask ${preview.inviterName} to send a new one.`}
      >
        <Button asChild>
          <Link to="/sign-in">Back to sign in</Link>
        </Button>
      </AuthLayout>
    );
  }
  if (preview.status === 'revoked') {
    return (
      <AuthLayout
        title="Invite revoked"
        subtitle={`This invite to ${preview.orgName} was revoked. Reach out to ${preview.inviterName} if that's a mistake.`}
      >
        <Button asChild>
          <Link to="/sign-in">Back to sign in</Link>
        </Button>
      </AuthLayout>
    );
  }
  if (preview.status === 'accepted') {
    return (
      <AuthLayout
        title="Already a member"
        subtitle={`You're already part of ${preview.orgName}. Sign in to switch into it.`}
      >
        <Button asChild>
          <Link to="/sign-in">Sign in</Link>
        </Button>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title={`Join ${preview.orgName}`}
      subtitle={`${preview.inviterName} invited you to join their team as ${preview.role}.`}
      footer={
        sessionStatus === 'authenticated' ? null : (
          <Link
            to={`/sign-in?next=${encodeURIComponent(`/invites/accept/${token}`)}`}
            className="font-medium text-foreground underline-offset-4 hover:underline"
          >
            Sign in to a different account
          </Link>
        )
      }
    >
      {error ? (
        <Alert variant="danger" className="mb-4">
          <AlertTitle>Couldn't accept</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      ) : null}

      {sessionStatus === 'authenticated' ? (
        <Button size="lg" className="w-full" loading={accepting} onClick={accept}>
          <CheckCircle2 className="size-4" /> Accept invite
        </Button>
      ) : (
        <div className="flex flex-col gap-3">
          <Button asChild size="lg">
            <Link to={`/sign-in?next=${encodeURIComponent(`/invites/accept/${token}`)}`}>
              Sign in to accept
            </Link>
          </Button>
          <Button asChild variant="outline">
            <Link to={`/sign-up?next=${encodeURIComponent(`/invites/accept/${token}`)}`}>
              Create an account <ExternalLink className="size-4" />
            </Link>
          </Button>
        </div>
      )}
    </AuthLayout>
  );
}
